import React from "react";
import styles from "./PrivacyPolicy.module.scss";

const LAST_UPDATED = "02-07-2026";

/* =========================================================================
   Policy content — structured so it renders in the site theme.
   block types: "p" (paragraph), "sub" (sub-heading), "ul" (bullet list)
========================================================================= */
const sections = [
  {
    heading: "Who We Are",
    content: [
      {
        type: "p",
        text: `Doql ("Doql", "we", "us") is a unified, auditable, enterprise layer for regulated industries—APIs, SaaS, and PaaS registered in India at 399A Shri Gopal Nagar, Gopalpura Bypass, Jaipur 302019, Rajasthan, India.`,
      },
      {
        type: "p",
        text: `This policy explains how we handle personal data. It covers two distinct roles:`,
      },
      {
        type: "ul",
        items: [
          `As a data CONTROLLER for our own business contacts and Client account users.`,
          `As a data PROCESSOR when we process end-customer personal data on behalf of, and under the instructions of, our Clients.`,
        ],
      },
    ],
  },
  {
    heading: "Scope",
    content: [
      {
        type: "p",
        text: `This policy applies to personal data we process through the Platform and our websites. Where we act as a processor for a Client, that Client is the data controller and their own privacy policy governs the end-customer relationship; we process such data only to provide the Platform to that Client.`,
      },
    ],
  },
  {
    heading: "Personal Data We Process",
    content: [
      {
        type: "sub",
        text: `As a controller (our Clients' account users and business contacts):`,
      },
      {
        type: "ul",
        items: [
          `Name, work email, phone, role, and account/usage data needed to provide and secure the Platform.`,
        ],
      },
      {
        type: "sub",
        text: `As a processor (end-customer data, on behalf of Clients):`,
      },
      {
        type: "ul",
        items: [
          `Identity & contact data: name, email address, phone number, postal/shipping address.`,
          `Commerce data: orders, order/fulfillment status, products viewed or enquired about, cart contents, and support conversation history.`,
        ],
      },
      {
        type: "p",
        text: `We request and process only the data necessary to deliver the contracted features (data minimisation).`,
      },
    ],
  },
  {
    heading: "How We Collect It",
    content: [
      {
        type: "ul",
        items: [
          `Directly from Clients during onboarding and use of the Platform.`,
          `From connected systems the Client authorises.`,
          `From end-customers' interactions with Client channels we operate on the Client's behalf (e.g., the chat widget).`,
        ],
      },
    ],
  },
  {
    heading: "Purposes of Processing",
    content: [
      {
        type: "ul",
        items: [
          `Provide customer support and respond to enquiries across web chat, email, phone and messaging.`,
          `Product discovery, recommendations and assisted shopping.`,
          `Order tracking, fulfillment status, and processing of replacements and refunds on the Client's instruction.`,
          `Where the end-customer has consented, transactional and marketing communications.`,
          `Securing, maintaining, and improving the Platform; fraud prevention; and legal compliance.`,
        ],
      },
    ],
  },
  {
    heading: "Legal Bases (where GDPR / similar laws apply)",
    content: [
      {
        type: "p",
        text: `Performance of a contract; legitimate interests (operating and securing the Platform); consent (for marketing); and compliance with legal obligations. Where we act as a processor, the Client is responsible for establishing the legal basis for the underlying processing.`,
      },
    ],
  },
  {
    heading: "How We Share Data / Sub-Processors",
    content: [
      {
        type: "p",
        text: `We do not sell personal data. We share it only with sub-processors strictly necessary to deliver the Platform, under data-processing agreements that require appropriate safeguards. Current sub-processors include:`,
      },
      {
        type: "ul",
        items: [
          `Zoho ZeptoMail — transactional/marketing email delivery.`,
          `GetGabs / WhatsApp Business — WhatsApp messaging.`,
          `Bolna — voice/telephony.`,
          `Amazon Web Services (AWS) — hosting and storage.`,
        ],
      },
      {
        type: "p",
        text: `We may also disclose data where required by law or to protect rights and safety.`,
      },
    ],
  },
  {
    heading: "International Transfers",
    content: [
      {
        type: "p",
        text: `The Platform is operated from India. Where personal data is transferred across borders, we apply appropriate safeguards (e.g., standard contractual clauses) as required by applicable law.`,
      },
    ],
  },
  {
    heading: "How We Protect Data",
    content: [
      {
        type: "ul",
        items: [
          `Encryption in transit (TLS 1.2+) and at rest (AES-256).`,
          `Encrypted storage of credentials and API tokens.`,
          `Role-based, least-privilege access; per-Client data isolation; and audit logging of access to personal data.`,
          `Documented incident-response and breach-notification procedures consistent with applicable law (e.g., GDPR 72-hour notification).`,
          `Personnel handling personal data receive data-protection training.`,
        ],
      },
    ],
  },
  {
    heading: "Data Retention",
    content: [
      {
        type: "p",
        text: `We retain personal data only as long as necessary for the purposes above. End-customer data processed on a Client's behalf is retained for the duration of that Client's active subscription and deleted within 30 days of contract termination, or upon a verified deletion request, unless a longer period is required by law.`,
      },
    ],
  },
  {
    heading: "Your Rights",
    content: [
      {
        type: "p",
        text: `Subject to applicable law, individuals may request access to, correction of, or deletion of their personal data, and may object to or restrict certain processing. Where we act as a processor, we will forward such requests to the relevant Client (controller) and assist them in responding.`,
      },
      {
        type: "p",
        text: `To exercise your rights, contact us at support@doql.com.`,
      },
    ],
  },
  {
    heading: "Cookies / Widget",
    content: [
      {
        type: "p",
        text: `Our embeddable chat widget may store a session identifier in the browser solely to maintain conversation continuity. It does not use advertising cookies.`,
      },
    ],
  },
  {
    heading: "Children",
    content: [
      {
        type: "p",
        text: `The Platform is not directed to children and we do not knowingly process children's personal data.`,
      },
    ],
  },
  {
    heading: "Changes to This Policy",
    content: [
      {
        type: "p",
        text: `We may update this policy from time to time. Material changes will be posted on this page with a revised "Last updated" date.`,
      },
    ],
  },
  {
    heading: "Contact & Grievance Officer",
    content: [
      { type: "sub", text: `Data Protection / Privacy contact:` },
      {
        type: "p",
        text: `Doql, 399A Shri Gopal Nagar, Gopalpura Bypass, Jaipur 302019, Rajasthan, India`,
      },
      { type: "p", text: `Email: support@doql.com` },
      {
        type: "sub",
        text: `Grievance Officer (as required under Indian law):`,
      },
      { type: "p", text: `Name: Dr. Naveen Kumar Sharma` },
      { type: "p", text: `Email: support@doql.com` },
      {
        type: "p",
        text: `We act as a data processor on behalf of our Clients for end-customer personal data; the Client is the data controller. For data tied to a specific store, please also refer to that store's own privacy policy.`,
      },
    ],
  },
];

const Block = ({ block }) => {
  if (block.type === "sub") {
    return <p className={styles["pp-subhead"]}>{block.text}</p>;
  }
  if (block.type === "ul") {
    return (
      <ul className={styles["pp-list"]}>
        {block.items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    );
  }
  return <p className={styles["pp-text"]}>{block.text}</p>;
};

export default function PrivacyPolicy() {
  return (
    <div className={styles["pp-root"]}>
      {/* HERO */}
      <section className={styles["pp-hero"]}>
        <div className={styles["pp-hero-ambient"]} aria-hidden />
        <div className={styles["pp-hero-grid"]} aria-hidden />
        <div className="sec-container">
          <span className={styles["pp-eyebrow"]}>Legal</span>
          <h1 className={styles["pp-title"]}>
            Privacy <span className={styles["pp-title-accent"]}>Policy</span>
          </h1>
          <p className={styles["pp-updated"]}>Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      {/* CONTENT */}
      <section className={styles["pp-main"]}>
        <div className="sec-container">
          <div className={styles["pp-doc"]}>
            {sections.map((section, i) => (
              <div className={styles["pp-section"]} key={i}>
                <h2 className={styles["pp-heading"]}>{section.heading}</h2>
                {section.content.map((block, j) => (
                  <Block block={block} key={j} />
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
